import { Cancel, Description, Download } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { getCessionCertificate, getDocumentReport } from 'lib/api/document';
import CessionHistoryDialog from 'lib/common/Dialogs/CessionHistoryDialog';
import {
  formatDate,
  getFilenameDateString,
  showAlert,
} from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import InfoRow from './InfoRow';
import InfoRowWithActions from './InfoRowWithActions';
import SectionSkeleton from './SectionSkeleton';

function GeneralSection({ documentId }: { documentId: string }) {
  const dispatch = useDispatch();
  const { data: documentProfile, isPending } = getDocumentReport(
    dispatch,
    documentId,
  );
  const [cessionsDialogOpen, setCessionsDialogOpen] = useState(false);
  const [loadingCessionCertificate, setLoadingCessionCertificate] =
    useState(false);

  if (!documentProfile || isPending)
    return <SectionSkeleton rowsPerColumn={4} />;

  const {
    businessIdentifier,
    stakeholderIdentifier,
    requestId,
    type,
    businessName,
    stakeholderName,
    folio,
    status,
    cessions,
  } = documentProfile;

  function getCessionDate() {
    const companyCessions = cessions.filter(
      (cession) => cession.giverIdentifier === businessIdentifier,
    );
    if (companyCessions.length === 0) return undefined;

    const latestCession = companyCessions[companyCessions.length - 1];
    return formatDate(new Date(latestCession.cessionDate), 'dd-mm-yyyy');
  }

  const cessionDate = getCessionDate();

  function download(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const handleDownloadCessionCertificate = async (id: string) => {
    try {
      setLoadingCessionCertificate(true);
      const blob = await getCessionCertificate(dispatch, id);
      if (!blob) {
        showAlert(dispatch, 'error', 'Error inesperado');
        return;
      }
      download(blob, `Certificado_Cesion_${getFilenameDateString()}.pdf`);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error inesperado');
      console.error(error);
    } finally {
      setLoadingCessionCertificate(false);
    }
  };

  const cessionDateActions = [
    ...(loadingCessionCertificate
      ? [
          {
            icon: <Cancel sx={{ fontSize: '1rem' }} />,
            onClick: () => {},
            tooltipText: 'Descargando...',
          },
        ]
      : [
          {
            icon: <Download sx={{ fontSize: '1rem' }} />,
            onClick: () => void handleDownloadCessionCertificate(documentId),
            tooltipText: 'Descargar certificado de cesión',
          },
        ]),
    {
      icon: <Description sx={{ fontSize: '1rem' }} />,
      onClick: () => setCessionsDialogOpen(true),
      tooltipText: 'Ver historial de cesiones',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          borderRadius: '8px',
          px: 1.5,
          py: 0.8,
          border: '1px solid rgba(25, 118, 210, 0.15)',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'primary.main',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          Datos Generales
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoRow
            label="Rut Cliente"
            value={businessIdentifier}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
          <InfoRow
            label="Rut Deudor"
            value={stakeholderIdentifier}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
          <InfoRow
            label="Nro. Operación"
            value={requestId}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
          <InfoRow
            label="Tipo de Documento"
            value={type}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
          <InfoRowWithActions
            label="Fecha cesión"
            value={cessionDate}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
            actions={cessionDate ? cessionDateActions : undefined}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoRow
            label="Nombre Cliente"
            value={businessName}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
          <InfoRow
            label="Nombre Deudor"
            value={stakeholderName}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
          <InfoRow
            label="Folio"
            value={folio}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
          <InfoRow
            label="Estado"
            value={status}
            maxLength={40}
            icon={
              <Description sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
            }
          />
        </Grid>
      </Grid>
      <CessionHistoryDialog
        open={cessionsDialogOpen}
        onClose={() => setCessionsDialogOpen(false)}
        cessions={cessions}
      />
    </Box>
  );
}

export default GeneralSection;
